import { useQuery } from "@tanstack/react-query";

import { backofficeClient } from "@/lib/api/backoffice-client";
import type { MetadataOption } from "@/hooks/use-itsm-metadata";

// Dropdown options for the project-management export-rule editors (Asana
// ENG-5072, monday ENG-2885, Linear ENG-6947). Served by service-desk rather than itsm-api:
// PM providers keep their client and token refresh there for the manual
// export, so the config endpoints sit alongside them.
//
// Unlike the ITSM metadata endpoints these take no integration_id — the
// service resolves the tenant's own credential.
//
// The providers differ only in vocabulary and in what they make of the
// workspace level (Asana pins it, monday filters by it, Linear has none),
// so one set of hooks covers all of them and takes the provider as an
// argument. Keeping them as one module (rather than a hook
// per provider picked at render time) means the component's hook call order
// never changes when a different credential is selected.

export interface PmScopesResponse {
  /** Asana only: the workspace the scopes belong to, echoed back so the
   *  picker can show which one answered. monday omits it — a board id is
   *  unique account-wide, so nothing downstream needs the workspace. */
  workspace_gid?: string;
  options: MetadataOption[];
}

interface OptionsResponse {
  options: MetadataOption[];
}

interface PmEndpoints {
  base: string;
  /** Path segment for the destination collection (projects / boards). */
  scopes: string;
  /** Path segment for the lane collection nested under a scope. */
  lanes: string;
  /** Query param naming the workspace when listing scopes. Absent for a
   *  provider with no workspace level: its scopes are listed unfiltered and
   *  the editor shows no workspace picker at all. */
  workspaceParam?: string;
  /** Whether the workspace is stored on the provider config.
   *
   *  Asana pins it: a project gid does not identify its workspace and task
   *  creation must name both. monday does not: `create_item` names only the
   *  board, so the workspace is a filter for this editor and nothing else —
   *  which is also why changing it leaves existing monday rules valid. */
  pinsWorkspace: boolean;
}

export const PM_ENDPOINTS: Record<string, PmEndpoints> = {
  asana: {
    base: "/proxy/service-desk/api/v1/export/asana/config",
    scopes: "projects",
    lanes: "sections",
    workspaceParam: "workspace_gid",
    pinsWorkspace: true,
  },
  monday: {
    base: "/proxy/service-desk/api/v1/export/monday/config",
    scopes: "boards",
    lanes: "groups",
    workspaceParam: "workspace_id",
    pinsWorkspace: false,
  },
  linear: {
    base: "/proxy/service-desk/api/v1/export/linear/config",
    scopes: "teams",
    lanes: "projects",
    // A Linear workspace is the credential itself — one token, one
    // workspace — so there is nothing to pick or pin.
    pinsWorkspace: false,
  },
};

/** What each provider calls a scope and a lane, for labels and copy. */
export interface PmVocabulary {
  scope: string;
  scopeTitle: string;
  lane: string;
  laneTitle: string;
  /** What the provider calls the thing created from a ticket. */
  item: string;
}

export const PM_VOCABULARY: Record<string, PmVocabulary> = {
  asana: {
    scope: "project",
    scopeTitle: "Project",
    lane: "section",
    laneTitle: "Section",
    item: "task",
  },
  monday: {
    scope: "board",
    scopeTitle: "Board",
    lane: "group",
    laneTitle: "Group",
    item: "item",
  },
  linear: {
    scope: "team",
    scopeTitle: "Team",
    lane: "project",
    laneTitle: "Project",
    item: "issue",
  },
};

export function pmEndpoints(provider: string): PmEndpoints {
  // eslint-disable-next-line security/detect-object-injection -- provider is a fixed union
  return PM_ENDPOINTS[provider] ?? PM_ENDPOINTS.asana;
}

export function pmVocabulary(provider: string): PmVocabulary {
  // eslint-disable-next-line security/detect-object-injection -- provider is a fixed union
  return PM_VOCABULARY[provider] ?? PM_VOCABULARY.asana;
}

/** Whether the provider has a workspace level above its scopes. */
export function pmHasWorkspaces(provider: string): boolean {
  return pmEndpoints(provider).workspaceParam !== undefined;
}

/** Workspaces the tenant's credential can reach.
 *
 * Asana credentials are often members of several, and which one the rules
 * point at has to be explicit. For monday this only narrows the board list.
 */
export function usePmWorkspaces(provider: string, tenantId?: string) {
  return useQuery({
    queryKey: ["pm-metadata", provider, "workspaces", tenantId],
    // Endpoints are resolved inside the query rather than closed over: they
    // are derived from `provider`, which is already in the key, so hoisting
    // them would only add key entries that can never vary independently.
    queryFn: async (): Promise<OptionsResponse> => {
      const { base } = pmEndpoints(provider);
      const { data } = await backofficeClient.get<OptionsResponse>(
        `${base}/workspaces`,
        { params: { tenant_id: tenantId } },
      );
      return data;
    },
    enabled: Boolean(tenantId) && pmHasWorkspaces(provider),
    staleTime: 5 * 60 * 1000,
  });
}

/** Destinations a desk can be routed to (Asana projects, monday boards,
 * Linear teams).
 *
 * The workspace is part of the query key, not just the request: scopes differ
 * per workspace, so a shared cache entry would offer one workspace's list
 * while another is selected.
 */
export function usePmScopes(
  provider: string,
  tenantId?: string,
  workspaceId?: string,
) {
  return useQuery({
    queryKey: ["pm-metadata", provider, "scopes", tenantId, workspaceId],
    queryFn: async (): Promise<PmScopesResponse> => {
      const { base, scopes, workspaceParam } = pmEndpoints(provider);
      const { data } = await backofficeClient.get<PmScopesResponse>(
        `${base}/${scopes}`,
        {
          params: {
            tenant_id: tenantId,
            ...(workspaceParam ? { [workspaceParam]: workspaceId } : {}),
          },
        },
      );
      return data;
    },
    enabled: Boolean(tenantId),
    staleTime: 5 * 60 * 1000, // metadata rarely changes within a session
  });
}

/** Lanes within a scope — the optional placement on an export rule. */
export function usePmLanes(
  provider: string,
  tenantId?: string,
  scopeId?: string,
) {
  return useQuery({
    queryKey: ["pm-metadata", provider, "lanes", tenantId, scopeId],
    queryFn: async (): Promise<OptionsResponse> => {
      const { base, scopes, lanes } = pmEndpoints(provider);
      const { data } = await backofficeClient.get<OptionsResponse>(
        `${base}/${scopes}/${encodeURIComponent(scopeId ?? "")}/${lanes}`,
        { params: { tenant_id: tenantId } },
      );
      return data;
    },
    enabled: Boolean(tenantId) && Boolean(scopeId),
    staleTime: 5 * 60 * 1000,
  });
}

/** A board column with monday's own column type (status, file, people…). */
export interface MondayColumnOption {
  value: string;
  label: string;
  column_type: string;
  /** Label texts, for status and dropdown columns only. */
  labels?: string[] | null;
}

interface MondayColumnsResponse {
  options: MondayColumnOption[];
  /** The board's own name and the workspace holding it. Best-effort, and
   *  absent from a service-desk that predates them, so every reader has a
   *  fallback. `workspace_id` is also null for a main workspace monday has
   *  not yet migrated to a real id, or when the credential cannot read
   *  workspaces. */
  board_name?: string | null;
  workspace_id?: string | null;
}

/** Every column on a monday board, for the two-way sync editors.
 *
 * monday-only rather than another PM hook: no other PM provider syncs
 * anything back, so there is no column concept to generalise over.
 */
export function useMondayColumns(tenantId?: string, boardId?: string) {
  return useQuery({
    queryKey: ["pm-metadata", "monday", "columns", tenantId, boardId],
    queryFn: async (): Promise<MondayColumnsResponse> => {
      const { base, scopes } = pmEndpoints("monday");
      const { data } = await backofficeClient.get<MondayColumnsResponse>(
        `${base}/${scopes}/${encodeURIComponent(boardId ?? "")}/columns`,
        { params: { tenant_id: tenantId } },
      );
      return data;
    },
    enabled: Boolean(tenantId) && Boolean(boardId),
    staleTime: 5 * 60 * 1000,
  });
}

/** Resolve a scope id to its name (falls back to the raw id).
 *
 * Takes the workspace so it looks the id up in the same list the rule was
 * chosen from; without it an Asana rule pointing outside the default
 * workspace would always fall back to showing its raw gid.
 *
 * A monday board is named by id instead, from its columns read. monday
 * answers an unscoped board list from a single workspace, and nothing
 * records which workspace a rule's board was picked from, so searching a
 * list would leave most boards of a many-workspace account as bare ids. The
 * list search stays as the fallback for a service-desk that predates the
 * name on the columns response.
 */
export function usePmScopeName(
  provider: string,
  tenantId: string,
  scopeId?: string | null,
  workspaceId?: string | null,
): string {
  const board = useMondayColumns(
    tenantId,
    provider === "monday" ? (scopeId ?? undefined) : undefined,
  );
  const { data } = usePmScopes(provider, tenantId, workspaceId ?? undefined);
  if (!scopeId) return "";
  return (
    board.data?.board_name ??
    data?.options.find((o) => o.value === scopeId)?.label ??
    scopeId
  );
}

/** Resolve a lane id to its name (falls back to the raw id). */
export function usePmLaneName(
  provider: string,
  tenantId: string,
  scopeId?: string | null,
  laneId?: string | null,
): string {
  const { data } = usePmLanes(provider, tenantId, scopeId ?? undefined);
  if (!laneId) return "";
  return data?.options.find((o) => o.value === laneId)?.label ?? laneId;
}
